import React, { useState } from 'react'
import { useDocuments } from 'contexts/documents'
import { useAccess } from 'contexts/access'
import { useCheckDocumentPermission } from 'pages/documents/utilities'
import { useUpgradable } from 'hooks/upgradable'
import { useIntl } from 'react-intl'
import paths from 'app/paths'
import { ContextMenu } from './s'
import Ellipsify from 'components/ellipsify'
import {
    Download,
    Link as CopyLink,
    Trash2 as Delete
} from 'styled-icons/feather'
import {
    SettingsDocument,
    Signature
} from 'components/feather'

const PreviewFileDocumentDetailsActions = ({
    dokument,
    setEditing,
    setConfiguringSigning,
    removeAction,
    dismiss,
    salt
}) => {
    const { formatMessage } = useIntl()

    const { checkFeature } = useAccess()
    const checkUpgradable = useUpgradable()

    const signingAvailable = checkFeature('digital-signing')
    const signingUpgradable = checkUpgradable({ feature: 'digital-signing' })

    const { editable, signable } = useCheckDocumentPermission()(dokument)

    const {
        getDocumentDownloadUrl,
        getSignedDocumentDownloadUrl,

        removeDocument
    } = useDocuments()

    let {
        id,
        name,
        extension,
        signing
    } = dokument

    const [destructing, setDestructing] = useState(false)

    const getDownloadUrl = (signing?.status === 'closed') ?
        getSignedDocumentDownloadUrl :
        getDocumentDownloadUrl

    const actions = {
        download: () => ({
            element: 'downloader',
            salt: `${salt}:download`,
            icon: <Download size={24} />,
            label: formatMessage({
                id: (signable && (signing?.status === 'closed')) ?
                    'document_action_download_signed' :
                    'action_download',
                defaultMessage: (signable && (signing?.status === 'closed')) ?
                    'Download signed' :
                    'Download'
            }),
            effect: 'neutral',
            download: {
                path: getDownloadUrl(id)
            }
        }),
        downloadOriginal: () => {
            if(!signable || (signing?.status !== 'closed')) {
                return null
            }

            return {
                element: 'downloader',
                salt: `${salt}:download-original`,
                icon: <Download size={24} />,
                label: formatMessage({
                    id: 'document_action_download_original',
                    defaultMessage: 'Download original'
                }),
                effect: 'neutral',
                download: {
                    path: getDocumentDownloadUrl(id)
                }
            }
        },
        edit: () => {
            if(!editable) {
                return null
            }

            return {
                salt: `${salt}:settings`,
                icon: <SettingsDocument size={24} />,
                label: formatMessage({
                    id: 'document_action_settings',
                    defaultMessage: 'Document settings'
                }),
                effect: 'neutral',
                onClick: () => setEditing('auto')
            }
        },
        configureSigning: () => {
            if(extension !== 'pdf' || (!signingAvailable && !signingUpgradable)) {
                return null
            }

            if(!signable) {
                return null
            }

            return {
                salt: `${salt}:configure-signing`,
                icon: <Signature size={24} />,
                label: formatMessage({
                    id: 'document_action_send_for_signing',
                    defaultMessage: 'Send for signing'
                }),
                effect: 'neutral',
                disabled: !!signing,
                onClick: () => setConfiguringSigning({ dokument }),
                ...(signingUpgradable ? {
                    upgrade: {
                        feature: 'digital-signing'
                    }
                } : null)
            }
        },
        copyLink: () => ({
            salt: `${salt}:copy-link`,
            icon: <CopyLink size={24} />,
            label: formatMessage({
                id: 'action_copy_link',
                defaultMessage: 'Copy link'
            }),
            effect: 'neutral',
            onClick: () => global.navigator.clipboard.writeText(`${window.location.origin}${paths.documents.base}?id=${dokument.id}`)
        }),
        remove: () => {
            if(!editable) {
                return null
            }

            const enrichOnClick = async remove => {
                setDestructing(true)
                const { ok } = await remove()
                setDestructing(false)

                ok && dismiss?.()
            }

            if(removeAction) {
                const action = removeAction(dokument)
                if(!action) {
                    return null
                }

                return {
                    ...action,
                    className: destructing ? 'loading' : null,
                    onClick: () => enrichOnClick(action.onClick)
                }
            }

            return {
                salt: `${salt}:delete`,
                icon: <Delete size={24} />,
                label: formatMessage({
                    id: 'action_delete',
                    defaultMessage: 'Delete',
                }),
                effect: 'destructive',
                className: destructing ? 'loading' : null,
                onClick: () => enrichOnClick(() => removeDocument(id)),
                prompt: {
                    question: formatMessage({
                        id: 'document_action_delete_prompt_question',
                        defaultMessage: 'Confirm deleting {document}'
                    }, {
                        document: (
                            <Ellipsify
                                text={`${name}.${extension}`}
                                head={9}
                                tail={9}
                                tooltipProps={{ maxWidth: 'none' }} />
                        )
                    }),
                    confirmText: formatMessage({
                        id: 'document_action_delete_prompt_confirm',
                        defaultMessage: 'Delete'
                    })
                }
            }
        }
    }

    return (
        <ContextMenu
            actions={actions}
            context={dokument}
            salt={salt} />
    )
}

export default PreviewFileDocumentDetailsActions